import { localize } from 'common/utils';

import {
  ExperienceBrandEntity,
  ExperienceEnterpriseEntity,
  ExperienceEntity,
  ExperienceEntityTree,
  ExperienceEntityType,
  ExperienceMerchantEntity,
  ExperienceVenueEntity,
} from './types';

export const localizedEntityName = (entity: ExperienceEntity, locale: string): string => {
  return typeof entity.experienceEntityObject.name === 'string'
    ? entity.experienceEntityObject.name
    : localize(entity.experienceEntityObject.name, locale);
};

export const getInitials = (label: string) =>
  label
    .split(' ')
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('');

type Entity<T extends ExperienceEntityType> = T extends ExperienceEntityType.VENUE
  ? ExperienceVenueEntity
  : T extends ExperienceEntityType.MERCHANT
    ? ExperienceMerchantEntity
    : T extends ExperienceEntityType.BRAND
      ? ExperienceBrandEntity
      : T extends ExperienceEntityType.ENTERPRISE
        ? ExperienceEnterpriseEntity
        : never;

const isEntityOfType = <T extends ExperienceEntityType>(
  experienceEntity: ExperienceEntity,
  experienceEntityType: T,
): experienceEntity is Entity<T> => experienceEntity.experienceEntityType === experienceEntityType;

export const recursiveGetOfType = <T extends ExperienceEntityType>(
  experienceEntityTree: ExperienceEntityTree,
  experienceEntityType: T,
): Entity<T>[] => {
  if (isEntityOfType(experienceEntityTree.experienceEntity, experienceEntityType)) {
    return [experienceEntityTree.experienceEntity];
  }

  return experienceEntityTree.children.flatMap((child: ExperienceEntityTree) =>
    recursiveGetOfType(child, experienceEntityType),
  );
};

export const getExperienceEntityCity = (experienceVenues: ExperienceVenueEntity[]): string | undefined => {
  if (experienceVenues.length === 0) {
    return;
  }

  const firstCity = experienceVenues[0].experienceEntityObject.city;
  const isSameCity = experienceVenues.every((venueEntity: ExperienceVenueEntity) => {
    return venueEntity.experienceEntityObject.city === firstCity;
  });
  return isSameCity ? firstCity : undefined;
};

export const recursiveFindEntity = (
  experienceEntityTree: ExperienceEntityTree,
  experienceEntityId: string,
): ExperienceEntityTree | undefined => {
  if (experienceEntityTree.experienceEntity.experienceEntityId === experienceEntityId) {
    return experienceEntityTree;
  }

  return experienceEntityTree.children
    .map((child: ExperienceEntityTree) => recursiveFindEntity(child, experienceEntityId))
    .find(Boolean);
};

export const buildNewExperienceEntityTreeFilteredByName = (
  experienceEntityTreeChildren: ExperienceEntityTree[],
  query: string,
  locale: string,
): ExperienceEntityTree[] => {
  return experienceEntityTreeChildren
    .map((experienceEntityTree: ExperienceEntityTree) => {
      const children = buildNewExperienceEntityTreeFilteredByName(experienceEntityTree.children, query, locale);
      const name = localizedEntityName(experienceEntityTree.experienceEntity, locale);
      const isMatch = name.toLowerCase().includes(query.toLowerCase());
      return isMatch || children.length > 0
        ? {
            ...experienceEntityTree,
            children,
          }
        : null;
    })
    .filter(Boolean) as ExperienceEntityTree[];
};
